import { Property, Lead, Client } from '../types';
import { supabase } from '../integrations/supabase/client';

export interface EmailMetrics {
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  converted: number;
  unsubscribed: number;
}

export interface EmailTemplate {
  subject: string;
  html: string;
  text?: string;
}

class EmailService {
  private fromEmail = 'naoresponda@foxvendas.com.br';
  private fromName = 'Fox Vendas';

  // Método base para enviar emails via Supabase Edge Function
  private async sendEmail(
    to: string,
    subject: string,
    html: string,
    text?: string
  ) {
    console.log('📤 Enviando email via Supabase Edge Function...');
    console.log('📧 Para:', to);
    console.log('📝 Assunto:', subject);
    
    try {
      const { data, error } = await supabase.functions.invoke('send-email', {
        body: {
          to,
          subject,
          html,
          text,
          fromName: this.fromName,
          fromEmail: this.fromEmail
        }
      });

      if (error) {
        console.error('❌ Erro na Edge Function:', error);
        return { success: false, error: error.message };
      }

      if (!data.success) {
        console.error('❌ Erro no envio:', data.error);
        return { success: false, error: data.error };
      }

      console.log('✅ Email enviado com sucesso:', data);
      return { success: true, id: data.id };
    } catch (error) {
      console.error('❌ Erro ao chamar Edge Function:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Erro desconhecido' };
    }
  }

  // Template para reativação de leads
  private generateReactivationTemplate(lead: Lead, properties: Property[]): EmailTemplate {
    const propertiesHtml = properties.map(property => `
      <div style="border: 1px solid #e5e7eb; border-radius: 8px; padding: 16px; margin: 16px 0; background: white;">
        <img src="${property.media.images[0]?.url || ''}" alt="${property.title}" style="width: 100%; height: 200px; object-fit: cover; border-radius: 8px; margin-bottom: 12px;">
        <h3 style="color: #1f2937; margin: 0 0 8px 0; font-size: 18px;">${property.title}</h3>
        <p style="color: #6b7280; margin: 0 0 8px 0; font-size: 14px;">
          📍 ${property.location.neighborhood}, ${property.location.city}
        </p>
        <div style="display: flex; gap: 16px; margin: 8px 0; font-size: 14px; color: #6b7280;">
          <span>🛏️ ${property.bedrooms} quartos</span>
          <span>🚗 ${property.garage.quantity} vagas</span>
          <span>📐 ${property.livingArea}m²</span>
        </div>
        <p style="color: #f97316; font-size: 20px; font-weight: bold; margin: 12px 0;">
          ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 }).format(property.listPrice)}
        </p>
        <a href="${property.detailViewUrl}" style="background: #f97316; color: white; padding: 8px 16px; text-decoration: none; border-radius: 6px; display: inline-block; font-size: 14px;">
          Ver Detalhes
        </a>
      </div>
    `).join('');

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Novas Oportunidades - Fox Vendas</title>
      </head>
      <body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
        
        <!-- Header -->
        <div style="text-align: center; margin-bottom: 32px; padding: 24px; background: linear-gradient(135deg, #f97316 0%, #ea580c 100%); border-radius: 12px;">
          <h1 style="color: white; margin: 0; font-size: 28px;">🏠 Fox Vendas</h1>
          <p style="color: #fed7aa; margin: 8px 0 0 0; font-size: 16px;">Encontramos imóveis perfeitos para você!</p>
        </div>

        <!-- Greeting -->
        <div style="margin-bottom: 24px;">
          <h2 style="color: #1f2937; margin: 0 0 16px 0;">Olá, ${lead.name}! 👋</h2>
          <p style="color: #4b5563; margin: 0; font-size: 16px;">
            Lembra quando você demonstrou interesse em imóveis? Temos ótimas novidades para você!
          </p>
        </div>

        <!-- Original Interest -->
        <div style="background: #f3f4f6; padding: 16px; border-radius: 8px; margin: 24px 0; border-left: 4px solid #f97316;">
          <h3 style="color: #1f2937; margin: 0 0 8px 0; font-size: 16px;">💭 Seu interesse original:</h3>
          <p style="color: #6b7280; margin: 0; font-style: italic;">"${lead.message}"</p>
        </div>

        <!-- Properties -->
        <div style="margin: 32px 0;">
          <h3 style="color: #1f2937; margin: 0 0 16px 0; font-size: 20px;">🎯 Imóveis selecionados para você:</h3>
          ${propertiesHtml}
        </div>

        <!-- CTA -->
        <div style="text-align: center; margin: 32px 0; padding: 24px; background: #f9fafb; border-radius: 8px;">
          <h3 style="color: #1f2937; margin: 0 0 16px 0;">Pronto para encontrar seu novo lar?</h3>
          <a href="https://foxvendas.com/busca" style="background: #f97316; color: white; padding: 12px 24px; text-decoration: none; border-radius: 8px; display: inline-block; font-weight: bold; font-size: 16px;">
            Ver Todos os Imóveis
          </a>
        </div>

        <!-- Contact -->
        <div style="margin: 32px 0; padding: 20px; background: #1f2937; border-radius: 8px; color: white; text-align: center;">
          <h3 style="margin: 0 0 16px 0; color: #f97316;">Fale Conosco</h3>
          <p style="margin: 0 0 8px 0;">📞 (11) 99999-9999</p>
          <p style="margin: 0 0 8px 0;">📧 contato@foxvendas.com</p>
          <p style="margin: 0; font-size: 14px; color: #9ca3af;">
            Estamos aqui para ajudar você a encontrar o imóvel dos seus sonhos!
          </p>
        </div>

        <!-- Footer -->
        <div style="text-align: center; margin-top: 32px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #6b7280;">
          <p style="margin: 0 0 8px 0;">© 2024 Fox Vendas. Todos os direitos reservados.</p>
          <p style="margin: 0;">
            <a href="#" style="color: #6b7280; text-decoration: none;">Descadastrar</a> | 
            <a href="#" style="color: #6b7280; text-decoration: none;">Política de Privacidade</a>
          </p>
        </div>

      </body>
      </html>
    `;

    const text = `
      Olá, ${lead.name}!

      Encontramos imóveis perfeitos para você na Fox Vendas!

      Seu interesse original: "${lead.message}"

      Imóveis selecionados:
      ${properties.map(p => `
      - ${p.title}
        📍 ${p.location.neighborhood}, ${p.location.city}
        🛏️ ${p.bedrooms} quartos | 🚗 ${p.garage.quantity} vagas | 📐 ${p.livingArea}m²
        💰 ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 }).format(p.listPrice)}
        🔗 ${p.detailViewUrl}
      `).join('\n')}

      Ver todos os imóveis: https://foxvendas.com/busca

      Contato:
      📞 (11) 99999-9999
      📧 contato@foxvendas.com

      © 2024 Fox Vendas
    `;

    return {
      subject: `🏠 ${lead.name}, encontramos imóveis perfeitos para você!`,
      html,
      text
    };
  }

  // Template para novo imóvel que combina com cliente
  private generateNewPropertyTemplate(client: Client, property: Property): EmailTemplate {
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Novo Imóvel - Fox Vendas</title>
      </head>
      <body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
        
        <!-- Header -->
        <div style="text-align: center; margin-bottom: 32px; padding: 24px; background: linear-gradient(135deg, #f97316 0%, #ea580c 100%); border-radius: 12px;">
          <h1 style="color: white; margin: 0; font-size: 28px;">🏠 Fox Vendas</h1>
          <p style="color: #fed7aa; margin: 8px 0 0 0; font-size: 16px;">Novo imóvel que combina com você!</p>
        </div>

        <!-- Greeting -->
        <div style="margin-bottom: 24px;">
          <h2 style="color: #1f2937; margin: 0 0 16px 0;">Olá, ${client.name}! ⭐</h2>
          <p style="color: #4b5563; margin: 0; font-size: 16px;">
            Acabamos de receber um imóvel que combina perfeitamente com o que você está procurando!
          </p>
        </div>

        <!-- Property -->
        <div style="border: 2px solid #f97316; border-radius: 12px; padding: 20px; margin: 24px 0; background: white;">
          <img src="${property.media.images[0]?.url || ''}" alt="${property.title}" style="width: 100%; height: 250px; object-fit: cover; border-radius: 8px; margin-bottom: 16px;">
          
          <h3 style="color: #1f2937; margin: 0 0 12px 0; font-size: 22px;">${property.title}</h3>
          
          <p style="color: #6b7280; margin: 0 0 16px 0; font-size: 16px;">
            📍 ${property.location.address}, ${property.location.streetNumber} - ${property.location.neighborhood}, ${property.location.city}
          </p>
          
          <div style="display: flex; gap: 20px; margin: 16px 0; font-size: 16px; color: #6b7280; flex-wrap: wrap;">
            <span>🛏️ ${property.bedrooms} quartos</span>
            <span>🛁 ${property.suites} suítes</span>
            <span>🚗 ${property.garage.quantity} vagas</span>
            <span>📐 ${property.livingArea}m²</span>
          </div>
          
          <p style="color: #f97316; font-size: 28px; font-weight: bold; margin: 20px 0;">
            ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 }).format(property.listPrice)}
          </p>

          ${property.propertyAdministrationFee ? `
            <p style="color: #6b7280; margin: 8px 0; font-size: 14px;">
              + Condomínio: ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 }).format(property.propertyAdministrationFee)}
            </p>
          ` : ''}
          
          <div style="margin: 20px 0;">
            <h4 style="color: #1f2937; margin: 0 0 12px 0;">✨ Principais características:</h4>
            <div style="display: flex; flex-wrap: wrap; gap: 8px;">
              ${property.features.slice(0, 6).map(feature => `
                <span style="background: #f3f4f6; color: #374151; padding: 4px 8px; border-radius: 4px; font-size: 12px;">${feature}</span>
              `).join('')}
            </div>
          </div>
          
          <p style="color: #4b5563; margin: 16px 0; line-height: 1.6;">
            ${property.description}
          </p>
          
          <div style="text-align: center; margin-top: 24px;">
            <a href="${property.detailViewUrl}" style="background: #f97316; color: white; padding: 12px 24px; text-decoration: none; border-radius: 8px; display: inline-block; font-weight: bold; font-size: 16px;">
              Ver Detalhes Completos
            </a>
          </div>
        </div>

        <!-- Why this matches -->
        <div style="background: #ecfdf5; padding: 20px; border-radius: 8px; margin: 24px 0; border-left: 4px solid #10b981;">
          <h3 style="color: #065f46; margin: 0 0 12px 0; font-size: 18px;">🎯 Por que este imóvel é perfeito para você:</h3>
          <ul style="color: #047857; margin: 0; padding-left: 20px;">
            ${client.interests.includes(property.propertyType) ? '<li>Tipo de imóvel que você procura</li>' : ''}
            ${property.listPrice >= client.budget.min && property.listPrice <= client.budget.max ? '<li>Dentro do seu orçamento</li>' : ''}
            ${client.location.toLowerCase().includes(property.location.city.toLowerCase()) || client.location.toLowerCase().includes(property.location.neighborhood.toLowerCase()) ? '<li>Na região do seu interesse</li>' : ''}
            <li>Características premium que você vai adorar</li>
          </ul>
        </div>

        <!-- CTA -->
        <div style="text-align: center; margin: 32px 0; padding: 24px; background: #1f2937; border-radius: 8px; color: white;">
          <h3 style="margin: 0 0 16px 0; color: #f97316;">Interessado? Vamos agendar uma visita!</h3>
          <p style="margin: 0 0 20px 0; color: #d1d5db;">Entre em contato conosco agora mesmo:</p>
          <div style="margin: 16px 0;">
            <a href="tel:+5511999999999" style="background: #10b981; color: white; padding: 10px 20px; text-decoration: none; border-radius: 6px; display: inline-block; margin: 0 8px; font-weight: bold;">
              📞 Ligar Agora
            </a>
            <a href="https://wa.me/5511999999999" style="background: #059669; color: white; padding: 10px 20px; text-decoration: none; border-radius: 6px; display: inline-block; margin: 0 8px; font-weight: bold;">
              💬 WhatsApp
            </a>
          </div>
        </div>

        <!-- Footer -->
        <div style="text-align: center; margin-top: 32px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #6b7280;">
          <p style="margin: 0 0 8px 0;">© 2024 Fox Vendas. Todos os direitos reservados.</p>
          <p style="margin: 0;">
            <a href="#" style="color: #6b7280; text-decoration: none;">Descadastrar</a> | 
            <a href="#" style="color: #6b7280; text-decoration: none;">Política de Privacidade</a>
          </p>
        </div>

      </body>
      </html>
    `;

    const text = `
      Olá, ${client.name}!

      Novo imóvel que combina com você na Fox Vendas!

      ${property.title}
      📍 ${property.location.address}, ${property.location.streetNumber} - ${property.location.neighborhood}, ${property.location.city}
      
      🛏️ ${property.bedrooms} quartos | 🛁 ${property.suites} suítes | 🚗 ${property.garage.quantity} vagas | 📐 ${property.livingArea}m²
      
      💰 ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 }).format(property.listPrice)}
      ${property.propertyAdministrationFee ? `+ Condomínio: ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 }).format(property.propertyAdministrationFee)}` : ''}

      Características: ${property.features.slice(0, 6).join(', ')}

      ${property.description}

      Ver detalhes: ${property.detailViewUrl}

      Contato:
      📞 (11) 99999-9999
      💬 WhatsApp: https://wa.me/5511999999999

      © 2024 Fox Vendas
    `;

    return {
      subject: `🏠 ${client.name}, novo imóvel perfeito para você em ${property.location.neighborhood}!`,
      html,
      text
    };
  }

  // Template para recomendação de imóvel específico
  private generatePropertyRecommendationTemplate(lead: Lead, property: Property): EmailTemplate {
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Imóvel Recomendado - Fox Vendas</title>
      </head>
      <body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
        
        <!-- Header -->
        <div style="text-align: center; margin-bottom: 32px; padding: 24px; background: linear-gradient(135deg, #f97316 0%, #ea580c 100%); border-radius: 12px;">
          <h1 style="color: white; margin: 0; font-size: 28px;">🏠 Fox Vendas</h1>
          <p style="color: #fed7aa; margin: 8px 0 0 0; font-size: 16px;">Encontramos o imóvel perfeito para você!</p>
        </div>

        <!-- Greeting -->
        <div style="margin-bottom: 24px;">
          <h2 style="color: #1f2937; margin: 0 0 16px 0;">Olá, ${lead.name}! ⭐</h2>
          <p style="color: #4b5563; margin: 0; font-size: 16px;">
            Baseado no seu interesse, selecionamos especialmente este imóvel que combina perfeitamente com o que você procura!
          </p>
        </div>

        <!-- Original Interest -->
        <div style="background: #f3f4f6; padding: 16px; border-radius: 8px; margin: 24px 0; border-left: 4px solid #f97316;">
          <h3 style="color: #1f2937; margin: 0 0 8px 0; font-size: 16px;">💭 Seu interesse original:</h3>
          <p style="color: #6b7280; margin: 0; font-style: italic;">"${lead.message}"</p>
        </div>

        <!-- Property Recommendation -->
        <div style="border: 2px solid #f97316; border-radius: 12px; padding: 20px; margin: 24px 0; background: white;">
          <div style="text-align: center; margin-bottom: 16px;">
            <h3 style="color: #f97316; margin: 0; font-size: 20px;">🎯 Imóvel Recomendado Especialmente para Você</h3>
          </div>
          
          <img src="${property.media.images[0]?.url || ''}" alt="${property.title}" style="width: 100%; height: 250px; object-fit: cover; border-radius: 8px; margin-bottom: 16px;">
          
          <h3 style="color: #1f2937; margin: 0 0 12px 0; font-size: 22px;">${property.title}</h3>
          
          <p style="color: #6b7280; margin: 0 0 16px 0; font-size: 16px;">
            📍 ${property.location.address}, ${property.location.streetNumber} - ${property.location.neighborhood}, ${property.location.city}
          </p>
          
          <div style="display: flex; gap: 20px; margin: 16px 0; font-size: 16px; color: #6b7280; flex-wrap: wrap;">
            <span>🛏️ ${property.bedrooms} quartos</span>
            <span>🛁 ${property.suites} suítes</span>
            <span>🚗 ${property.garage.quantity} vagas</span>
            <span>📐 ${property.livingArea}m²</span>
          </div>
          
          <p style="color: #f97316; font-size: 28px; font-weight: bold; margin: 20px 0; text-align: center;">
            ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 }).format(property.listPrice)}
          </p>

          ${property.propertyAdministrationFee ? `
            <p style="color: #6b7280; margin: 8px 0; font-size: 14px; text-align: center;">
              + Condomínio: ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 }).format(property.propertyAdministrationFee)}
            </p>
          ` : ''}
          
          <div style="margin: 20px 0;">
            <h4 style="color: #1f2937; margin: 0 0 12px 0;">✨ Principais características:</h4>
            <div style="display: flex; flex-wrap: wrap; gap: 8px;">
              ${property.features.slice(0, 6).map(feature => `
                <span style="background: #f3f4f6; color: #374151; padding: 4px 8px; border-radius: 4px; font-size: 12px;">${feature}</span>
              `).join('')}
            </div>
          </div>
          
          <p style="color: #4b5563; margin: 16px 0; line-height: 1.6;">
            ${property.description}
          </p>
          
          <div style="text-align: center; margin-top: 24px;">
            <a href="${property.detailViewUrl}?lead=${lead.id}" style="background: #f97316; color: white; padding: 12px 24px; text-decoration: none; border-radius: 8px; display: inline-block; font-weight: bold; font-size: 16px; margin: 0 8px 8px 0;">
              Ver Detalhes Completos
            </a>
            <a href="https://foxvendas.com/interesse?property=${property.listingId}&lead=${lead.id}" style="background: #10b981; color: white; padding: 12px 24px; text-decoration: none; border-radius: 8px; display: inline-block; font-weight: bold; font-size: 16px; margin: 0 8px 8px 0;">
              Tenho Interesse!
            </a>
          </div>
        </div>

        <!-- CTA -->
        <div style="text-align: center; margin: 32px 0; padding: 24px; background: #1f2937; border-radius: 8px; color: white;">
          <h3 style="margin: 0 0 16px 0; color: #f97316;">Gostou? Vamos agendar uma visita!</h3>
          <p style="margin: 0 0 20px 0; color: #d1d5db;">Entre em contato conosco agora mesmo:</p>
          <div style="margin: 16px 0;">
            <a href="tel:+5511999999999" style="background: #10b981; color: white; padding: 10px 20px; text-decoration: none; border-radius: 6px; display: inline-block; margin: 0 8px; font-weight: bold;">
              📞 Ligar Agora
            </a>
            <a href="https://wa.me/5511999999999" style="background: #059669; color: white; padding: 10px 20px; text-decoration: none; border-radius: 6px; display: inline-block; margin: 0 8px; font-weight: bold;">
              💬 WhatsApp
            </a>
          </div>
        </div>

        <!-- Footer -->
        <div style="text-align: center; margin-top: 32px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #6b7280;">
          <p style="margin: 0 0 8px 0;">© 2024 Fox Vendas. Todos os direitos reservados.</p>
          <p style="margin: 0;">
            <a href="#" style="color: #6b7280; text-decoration: none;">Descadastrar</a> | 
            <a href="#" style="color: #6b7280; text-decoration: none;">Política de Privacidade</a>
          </p>
        </div>

      </body>
      </html>
    `;

    const text = `
      Olá, ${lead.name}!

      Encontramos o imóvel perfeito para você na Fox Vendas!

      Seu interesse original: "${lead.message}"

      🎯 IMÓVEL RECOMENDADO:
      ${property.title}
      📍 ${property.location.address}, ${property.location.streetNumber} - ${property.location.neighborhood}, ${property.location.city}
      
      🛏️ ${property.bedrooms} quartos | 🛁 ${property.suites} suítes | 🚗 ${property.garage.quantity} vagas | 📐 ${property.livingArea}m²
      
      💰 ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 }).format(property.listPrice)}
      ${property.propertyAdministrationFee ? `+ Condomínio: ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 }).format(property.propertyAdministrationFee)}` : ''}

      Características: ${property.features.slice(0, 6).join(', ')}

      ${property.description}

      Ver detalhes: ${property.detailViewUrl}?lead=${lead.id}
      Demonstrar interesse: https://foxvendas.com/interesse?property=${property.listingId}&lead=${lead.id}

      Contato:
      📞 (11) 99999-9999
      💬 WhatsApp: https://wa.me/5511999999999

      © 2024 Fox Vendas
    `;

    return {
      subject: `🏠 ${lead.name}, encontramos o imóvel perfeito para você em ${property.location.neighborhood}!`,
      html,
      text
    };
  }

  // Template "Sentimos sua falta"
  private generateMissYouTemplate(lead: Lead, broker: any): EmailTemplate {
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Sentimos sua Falta - Fox Vendas</title>
      </head>
      <body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
        
        <!-- Header -->
        <div style="text-align: center; margin-bottom: 32px; padding: 24px; background: linear-gradient(135deg, #ec4899 0%, #be185d 100%); border-radius: 12px;">
          <h1 style="color: white; margin: 0; font-size: 28px;">💕 Fox Vendas</h1>
          <p style="color: #fce7f3; margin: 8px 0 0 0; font-size: 16px;">Sentimos muito a sua falta!</p>
        </div>

        <!-- Main Message -->
        <div style="margin-bottom: 32px; text-align: center;">
          <h2 style="color: #1f2937; margin: 0 0 24px 0; font-size: 32px;">Olá, ${lead.name}! 👋</h2>
          
          <div style="background: #fef3f2; border: 2px solid #fecaca; border-radius: 12px; padding: 24px; margin: 24px 0;">
            <h3 style="color: #dc2626; margin: 0 0 16px 0; font-size: 24px;">❤️ Sentimos muito a sua falta!</h3>
            <p style="color: #7f1d1d; margin: 0; font-size: 18px; line-height: 1.6;">
              Notamos que você demonstrou interesse em encontrar um imóvel conosco, mas perdemos o contato. 
              Isso nos deixou preocupados! 😔
            </p>
          </div>

          <p style="color: #4b5563; font-size: 18px; margin: 24px 0; line-height: 1.6;">
            Sabemos como pode ser desafiador encontrar o imóvel dos sonhos, e queremos muito ajudar você nessa jornada importante. 
            Nossa equipe está aqui para tornar esse processo mais fácil e prazeroso! ✨
          </p>
        </div>

        <!-- Original Interest -->
        <div style="background: #f3f4f6; padding: 20px; border-radius: 8px; margin: 24px 0; border-left: 4px solid #ec4899;">
          <h3 style="color: #1f2937; margin: 0 0 12px 0; font-size: 18px;">💭 Lembramos do seu interesse:</h3>
          <p style="color: #6b7280; margin: 0; font-style: italic; font-size: 16px;">"${lead.message}"</p>
        </div>

        <!-- New Broker -->
        <div style="background: #ecfdf5; padding: 20px; border-radius: 8px; margin: 24px 0; border-left: 4px solid #10b981;">
          <h3 style="color: #065f46; margin: 0 0 12px 0; font-size: 18px;">👨‍💼 Seu novo consultor especializado:</h3>
          <p style="color: #047857; margin: 0; font-size: 16px;">
            <strong>${broker.firstName} ${broker.lastName}</strong> está pronto para ajudar você a encontrar o imóvel perfeito!
          </p>
          <p style="color: #047857; margin: 8px 0 0 0; font-size: 14px;">
            📧 ${broker.email} | 📞 ${broker.phone}
          </p>
        </div>

        <!-- What We Offer -->
        <div style="margin: 32px 0;">
          <h3 style="color: #1f2937; margin: 0 0 20px 0; font-size: 20px; text-align: center;">🎯 Como podemos ajudar você:</h3>
          <div style="display: grid; gap: 16px;">
            <div style="display: flex; align-items: start; padding: 16px; background: #f9fafb; border-radius: 8px;">
              <span style="font-size: 24px; margin-right: 12px;">🔍</span>
              <div>
                <h4 style="color: #1f2937; margin: 0 0 8px 0; font-size: 16px;">Busca Personalizada</h4>
                <p style="color: #6b7280; margin: 0; font-size: 14px;">Encontramos imóveis que combinam exatamente com seu perfil e orçamento</p>
              </div>
            </div>
            <div style="display: flex; align-items: start; padding: 16px; background: #f9fafb; border-radius: 8px;">
              <span style="font-size: 24px; margin-right: 12px;">📅</span>
              <div>
                <h4 style="color: #1f2937; margin: 0 0 8px 0; font-size: 16px;">Visitas Agendadas</h4>
                <p style="color: #6b7280; margin: 0; font-size: 14px;">Organizamos visitas nos horários que funcionam para você</p>
              </div>
            </div>
            <div style="display: flex; align-items: start; padding: 16px; background: #f9fafb; border-radius: 8px;">
              <span style="font-size: 24px; margin-right: 12px;">💰</span>
              <div>
                <h4 style="color: #1f2937; margin: 0 0 8px 0; font-size: 16px;">Negociação Especializada</h4>
                <p style="color: #6b7280; margin: 0; font-size: 14px;">Ajudamos você a conseguir as melhores condições</p>
              </div>
            </div>
          </div>
        </div>

        <!-- CTA -->
        <div style="text-align: center; margin: 32px 0; padding: 32px; background: linear-gradient(135deg, #ec4899 0%, #be185d 100%); border-radius: 12px; color: white;">
          <h3 style="margin: 0 0 16px 0; font-size: 24px;">💕 Vamos recomeçar juntos?</h3>
          <p style="margin: 0 0 24px 0; color: #fce7f3; font-size: 16px;">
            Clique no botão abaixo e nossa equipe entrará em contato em até 1 hora!
          </p>
          <a href="${window.location.origin}/reativar?lead=${lead.id}&broker=${broker.id}" style="background: white; color: #ec4899; padding: 16px 32px; text-decoration: none; border-radius: 8px; display: inline-block; font-weight: bold; font-size: 18px;">
            ❤️ Sim, quero ajuda para encontrar meu imóvel!
          </a>
        </div>

        <!-- Footer -->
        <div style="text-align: center; margin-top: 32px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #6b7280;">
          <p style="margin: 0 0 8px 0;">© 2024 Fox Vendas. Todos os direitos reservados.</p>
          <p style="margin: 0;">
            <a href="#" style="color: #6b7280; text-decoration: none;">Descadastrar</a> | 
            <a href="#" style="color: #6b7280; text-decoration: none;">Política de Privacidade</a>
          </p>
        </div>

      </body>
      </html>
    `;

    const text = `
      Olá, ${lead.name}!

      💕 SENTIMOS MUITO A SUA FALTA!

      Notamos que você demonstrou interesse em encontrar um imóvel conosco, mas perdemos o contato. Isso nos deixou preocupados!

      Lembramos do seu interesse: "${lead.message}"

      👨‍💼 SEU NOVO CONSULTOR:
      ${broker.firstName} ${broker.lastName}
      📧 ${broker.email}
      📞 ${broker.phone}

      🎯 COMO PODEMOS AJUDAR:
      🔍 Busca personalizada de imóveis
      📅 Visitas agendadas no seu horário
      💰 Negociação especializada

      Clique aqui para reativar seu atendimento:
      ${window.location.origin}/reativar?lead=${lead.id}&broker=${broker.id}

      © 2024 Fox Vendas
    `;

    return {
      subject: `💕 ${lead.name}, sentimos muito a sua falta! Vamos te ajudar?`,
      html,
      text
    };
  }

  // Template "Ainda está procurando?"
  private generateStillLookingTemplate(lead: Lead, broker: any): EmailTemplate {
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Ainda Procurando? - Fox Vendas</title>
      </head>
      <body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
        
        <!-- Header -->
        <div style="text-align: center; margin-bottom: 32px; padding: 24px; background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); border-radius: 12px;">
          <h1 style="color: white; margin: 0; font-size: 28px;">🔍 Fox Vendas</h1>
          <p style="color: #e9d5ff; margin: 8px 0 0 0; font-size: 16px;">Novas oportunidades esperando por você!</p>
        </div>

        <!-- Main Question -->
        <div style="margin-bottom: 32px; text-align: center;">
          <h2 style="color: #1f2937; margin: 0 0 24px 0; font-size: 32px;">Olá, ${lead.name}! 🤔</h2>
          
          <div style="background: #faf5ff; border: 2px solid #d8b4fe; border-radius: 12px; padding: 24px; margin: 24px 0;">
            <h3 style="color: #7c3aed; margin: 0 0 16px 0; font-size: 24px;">🏠 Ainda está procurando um imóvel?</h3>
            <p style="color: #581c87; margin: 0; font-size: 18px; line-height: 1.6;">
              Sabemos que encontrar o imóvel ideal pode levar tempo, e queremos saber se ainda podemos ajudar você nessa busca! 
            </p>
          </div>

          <p style="color: #4b5563; font-size: 18px; margin: 24px 0; line-height: 1.6;">
            Temos recebido <strong>muitas oportunidades novas</strong> e não queremos que você perca nenhuma chance de encontrar seu imóvel dos sonhos! 🌟
          </p>
        </div>

        <!-- Original Interest -->
        <div style="background: #f3f4f6; padding: 20px; border-radius: 8px; margin: 24px 0; border-left: 4px solid #8b5cf6;">
          <h3 style="color: #1f2937; margin: 0 0 12px 0; font-size: 18px;">💭 Seu interesse original:</h3>
          <p style="color: #6b7280; margin: 0; font-style: italic; font-size: 16px;">"${lead.message}"</p>
        </div>

        <!-- New Opportunities -->
        <div style="margin: 32px 0;">
          <h3 style="color: #1f2937; margin: 0 0 20px 0; font-size: 20px; text-align: center;">🆕 Novas oportunidades disponíveis:</h3>
          <div style="display: grid; gap: 16px;">
            <div style="display: flex; align-items: start; padding: 16px; background: #faf5ff; border-radius: 8px; border-left: 4px solid #8b5cf6;">
              <span style="font-size: 24px; margin-right: 12px;">🏢</span>
              <div>
                <h4 style="color: #1f2937; margin: 0 0 8px 0; font-size: 16px;">Novos Lançamentos</h4>
                <p style="color: #6b7280; margin: 0; font-size: 14px;">Empreendimentos exclusivos com condições especiais</p>
              </div>
            </div>
            <div style="display: flex; align-items: start; padding: 16px; background: #faf5ff; border-radius: 8px; border-left: 4px solid #8b5cf6;">
              <span style="font-size: 24px; margin-right: 12px;">💰</span>
              <div>
                <h4 style="color: #1f2937; margin: 0 0 8px 0; font-size: 16px;">Oportunidades de Investimento</h4>
                <p style="color: #6b7280; margin: 0; font-size: 14px;">Imóveis com excelente potencial de valorização</p>
              </div>
            </div>
            <div style="display: flex; align-items: start; padding: 16px; background: #faf5ff; border-radius: 8px; border-left: 4px solid #8b5cf6;">
              <span style="font-size: 24px; margin-right: 12px;">🔥</span>
              <div>
                <h4 style="color: #1f2937; margin: 0 0 8px 0; font-size: 16px;">Ofertas Especiais</h4>
                <p style="color: #6b7280; margin: 0; font-size: 14px;">Preços promocionais e condições facilitadas</p>
              </div>
            </div>
          </div>
        </div>

        <!-- New Broker -->
        <div style="background: #ecfdf5; padding: 20px; border-radius: 8px; margin: 24px 0; border-left: 4px solid #10b981;">
          <h3 style="color: #065f46; margin: 0 0 12px 0; font-size: 18px;">👨‍💼 Consultor especializado pronto para ajudar:</h3>
          <p style="color: #047857; margin: 0; font-size: 16px;">
            <strong>${broker.firstName} ${broker.lastName}</strong> tem acesso exclusivo às melhores oportunidades do mercado!
          </p>
          <p style="color: #047857; margin: 8px 0 0 0; font-size: 14px;">
            📧 ${broker.email} | 📞 ${broker.phone}
          </p>
        </div>

        <!-- CTA -->
        <div style="text-align: center; margin: 32px 0; padding: 32px; background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); border-radius: 12px; color: white;">
          <h3 style="margin: 0 0 16px 0; font-size: 24px;">🚀 Sim, ainda estou procurando!</h3>
          <p style="margin: 0 0 24px 0; color: #e9d5ff; font-size: 16px;">
            Clique no botão abaixo e receba as melhores oportunidades em primeira mão!
          </p>
          <a href="${window.location.origin}/reativar?lead=${lead.id}&broker=${broker.id}&type=still-looking" style="background: white; color: #8b5cf6; padding: 16px 32px; text-decoration: none; border-radius: 8px; display: inline-block; font-weight: bold; font-size: 18px;">
            🔍 Sim, quero ver as novas oportunidades!
          </a>
        </div>

        <!-- Alternative -->
        <div style="text-align: center; margin: 24px 0; padding: 20px; background: #f9fafb; border-radius: 8px;">
          <p style="color: #6b7280; margin: 0 0 12px 0; font-size: 14px;">
            Já encontrou seu imóvel ou não tem mais interesse?
          </p>
          <a href="#" style="color: #8b5cf6; text-decoration: none; font-size: 14px;">
            Clique aqui para não receber mais emails
          </a>
        </div>

        <!-- Footer -->
        <div style="text-align: center; margin-top: 32px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #6b7280;">
          <p style="margin: 0 0 8px 0;">© 2024 Fox Vendas. Todos os direitos reservados.</p>
          <p style="margin: 0;">
            <a href="#" style="color: #6b7280; text-decoration: none;">Descadastrar</a> | 
            <a href="#" style="color: #6b7280; text-decoration: none;">Política de Privacidade</a>
          </p>
        </div>

      </body>
      </html>
    `;

    const text = `
      Olá, ${lead.name}!

      🔍 AINDA ESTÁ PROCURANDO UM IMÓVEL?

      Sabemos que encontrar o imóvel ideal pode levar tempo, e queremos saber se ainda podemos ajudar você nessa busca!

      Temos recebido muitas oportunidades novas e não queremos que você perca nenhuma chance!

      Seu interesse original: "${lead.message}"

      🆕 NOVAS OPORTUNIDADES:
      🏢 Novos lançamentos exclusivos
      💰 Oportunidades de investimento
      🔥 Ofertas especiais

      👨‍💼 SEU CONSULTOR:
      ${broker.firstName} ${broker.lastName}
      📧 ${broker.email}
      📞 ${broker.phone}

      Clique aqui para reativar e ver as novas oportunidades:
      ${window.location.origin}/reativar?lead=${lead.id}&broker=${broker.id}&type=still-looking

      © 2024 Fox Vendas
    `;

    return {
      subject: `🔍 ${lead.name}, ainda está procurando? Temos novas oportunidades!`,
      html,
      text
    };
  }

  // Enviar email de reativação para lead perdido
  async sendReactivationEmail(lead: Lead, matchingProperties: Property[]) {
    if (!lead.email) {
      return { success: false, error: 'Lead não possui email' };
    }

    const template = this.generateReactivationTemplate(lead, matchingProperties);
    return await this.sendEmail(lead.email, template.subject, template.html, template.text);
  }

  // Enviar alerta de novo imóvel para cliente
  async sendNewPropertyAlert(client: Client, property: Property) {
    const template = this.generateNewPropertyTemplate(client, property);
    return await this.sendEmail(client.email, template.subject, template.html, template.text);
  }

  // Enviar email de recomendação de imóvel específico
  async sendPropertyRecommendationEmail(lead: Lead, property: Property) {
    if (!lead.email) {
      return { success: false, error: 'Lead não possui email' };
    }

    const template = this.generatePropertyRecommendationTemplate(lead, property);
    return await this.sendEmail(lead.email, template.subject, template.html, template.text);
  }

  // Enviar email "Sentimos sua falta"
  async sendMissYouEmail(lead: Lead, broker: any) {
    if (!lead.email) {
      return { success: false, error: 'Lead não possui email' };
    }

    const template = this.generateMissYouTemplate(lead, broker);
    return await this.sendEmail(lead.email, template.subject, template.html, template.text);
  }

  // Enviar email "Ainda está procurando?"
  async sendStillLookingEmail(lead: Lead, broker: any) {
    if (!lead.email) {
      return { success: false, error: 'Lead não possui email' };
    }

    const template = this.generateStillLookingTemplate(lead, broker);
    return await this.sendEmail(lead.email, template.subject, template.html, template.text);
  }

  // Enviar email de follow-up
  async sendFollowUpEmail(lead: Lead, type: 'immediate' | 'detailed' | 'similar' | 'market') {
    // Implementar diferentes tipos de follow-up
    const templates = {
      immediate: {
        subject: `${lead.name}, obrigado pelo seu interesse!`,
        html: `<p>Recebemos seu interesse e entraremos em contato em breve...</p>`
      },
      detailed: {
        subject: `Mais detalhes sobre os imóveis que você procura`,
        html: `<p>Aqui estão mais informações...</p>`
      },
      similar: {
        subject: `Outras opções que podem interessar você`,
        html: `<p>Veja estas alternativas...</p>`
      },
      market: {
        subject: `Relatório do mercado imobiliário`,
        html: `<p>Confira as tendências do mercado...</p>`
      }
    };

    if (!lead.email) {
      return { success: false, error: 'Lead não possui email' };
    }

    const template = templates[type];
    return await this.sendEmail(lead.email, template.subject, template.html);
  }

  // Encontrar imóveis que combinam com lead/cliente
  findMatchingProperties(leadOrClient: Lead | Client, allProperties: Property[]): Property[] {
    // Lógica básica de matching - pode ser expandida
    return allProperties.filter(property => {
      // Se for cliente, usar critérios mais específicos
      if ('budget' in leadOrClient) {
        const client = leadOrClient as Client;
        const withinBudget = property.listPrice >= client.budget.min && property.listPrice <= client.budget.max;
        const matchesLocation = client.location ? 
          property.location.city.toLowerCase().includes(client.location.toLowerCase()) ||
          property.location.neighborhood.toLowerCase().includes(client.location.toLowerCase()) : true;
        const matchesInterests = client.interests.length === 0 || 
          client.interests.some(interest => property.propertyType.includes(interest));
        
        return withinBudget && matchesLocation && matchesInterests;
      }
      
      // Para leads, usar matching mais simples baseado na mensagem
      const lead = leadOrClient as Lead;
      const message = lead.message.toLowerCase();
      const matchesMessage = 
        property.title.toLowerCase().includes(message) ||
        property.location.neighborhood.toLowerCase().includes(message) ||
        property.location.city.toLowerCase().includes(message) ||
        property.propertyType.toLowerCase().includes(message);
      
      return matchesMessage;
    }).slice(0, 5); // Limitar a 5 imóveis por email
  }

  // Método público para teste de email
  async sendTestEmail(testEmail: string) {
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Teste de Email</title>
      </head>
      <body style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
        <div style="background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); padding: 20px; border-radius: 10px; text-align: center; margin-bottom: 20px;">
          <h1 style="color: white; margin: 0;">🧪 Teste de Email</h1>
          <p style="color: #e9d5ff; margin: 10px 0 0 0;">Sistema Fox Vendas</p>
        </div>
        
        <div style="background: #f8fafc; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
          <h2 style="color: #1f2937; margin: 0 0 15px 0;">✅ Email funcionando perfeitamente!</h2>
          <p style="color: #4b5563; margin: 0; line-height: 1.6;">
            Se você recebeu este email, significa que a integração do Resend com Supabase está funcionando corretamente.
          </p>
        </div>
        
        <div style="background: #ecfdf5; border: 1px solid #d1fae5; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
          <h3 style="color: #065f46; margin: 0 0 10px 0; font-size: 16px;">📊 Detalhes do Teste:</h3>
          <ul style="color: #047857; margin: 0; padding-left: 20px;">
            <li>Data/Hora: ${new Date().toLocaleString('pt-BR')}</li>
            <li>Remetente: ${this.fromName} &lt;${this.fromEmail}&gt;</li>
            <li>Destinatário: ${testEmail}</li>
            <li>Sistema: Fox Vendas via Supabase Edge Function</li>
          </ul>
        </div>
        
        <div style="text-align: center; padding: 20px; background: #1f2937; border-radius: 8px; color: white;">
          <p style="margin: 0; font-size: 14px;">
            © 2024 Fox Vendas - Sistema de Gestão Imobiliária
          </p>
        </div>
      </body>
      </html>
    `;

    const text = `
🧪 TESTE DE EMAIL - FOX VENDAS

✅ Email funcionando perfeitamente!

Se você recebeu este email, significa que a integração do Resend com Supabase está funcionando corretamente.

📊 DETALHES DO TESTE:
• Data/Hora: ${new Date().toLocaleString('pt-BR')}
• Remetente: ${this.fromName} <${this.fromEmail}>
• Destinatário: ${testEmail}
• Sistema: Fox Vendas via Supabase Edge Function

© 2024 Fox Vendas - Sistema de Gestão Imobiliária
    `;

    return this.sendEmail(
      testEmail,
      '🧪 Teste de Email - Fox Vendas',
      html,
      text
    );
  }
}

export const emailService = new EmailService();